import React, { useEffect, useRef, useState } from 'react';
import {
  Form,
  Input,
  Button,
  Card,
  Typography,
  Modal,
  Select,
  Tag,
  Table,
  message,
  Space,
  Row,
  Col,
  Tooltip,
  Image,
  DatePicker,
  Descriptions,
  Progress
} from 'antd';
import dayjs from 'dayjs';
import moment from 'moment';
import {
  apiGetDevicesInformation,
  apiGetLinkDown,
  apiCreateLinkDown,
  apiDeleteLinkDown,
  apiExportAdjustReport
} from '@services/ManageDevices/DeviceServices';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  MinusCircleOutlined,
  SyncOutlined,
  ToolOutlined,
  FileExcelOutlined,
  EyeOutlined
} from '@ant-design/icons';
import * as Yup from 'yup';
import { formatNumber, isNullOrEmpty, downloadBlobFile } from '@utils/utils';
import { URL_API } from '@configs/index';
import { Formik } from 'formik';
import InputUploadImage from '@components/Upload/Upload';
import { usePermission } from '@contexts/Auth/UsePermission';

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;

const validationSchema = Yup.object().shape({
  imei: Yup.string().required('Vui lòng chọn thiết bị!'),
  firstValue: Yup.string().required('Vui lòng nhập số khối!'),
  secondValue: Yup.string()
    .max(3, 'Chỉ được nhập tối đa 3 ký tự')
    .matches(/^\d+$/, 'Chỉ được nhập số')
});

function getStatusTag(item) {
  const now = new Date();
  const sentAt = new Date(item?.sent_at);
  const isExpired = now.getTime() - 60000 > sentAt.getTime();
  if (item?.is_sent === 2) {
    return (
      <Tag icon={<CheckCircleOutlined />} color="success">
        Đã gửi
      </Tag>
    );
  }
  if (item?.is_sent === 1) {
    return isExpired ? (
      <Tag icon={<MinusCircleOutlined />} color="error">
        Thất bại
      </Tag>
    ) : (
      <Tag icon={<SyncOutlined spin />} color="processing">
        Đang gửi
      </Tag>
    );
  }
  return (
    <Tag icon={<CloseCircleOutlined />} color="default">
      Chưa gửi
    </Tag>
  );
}

const sectionLabelStyle = {
  fontSize: 12,
  color: 'var(--muted)',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: 0.5
};

export default function AdjustMeterReadingCommand() {
  const { adjustMeterReading } = usePermission();
  const [deviceList, setDeviceList] = useState([]);
  const [historicalCmd, setHistoricalCmd] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isDelete, setIsDelete] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [detailRecord, setDetailRecord] = useState(null);

  const [filterKeyword, setFilterKeyword] = useState('');
  const [filterDateRange, setFilterDateRange] = useState([null, null]);
  const [filterStatus, setFilterStatus] = useState('');
  const [appliedFilters, setAppliedFilters] = useState({ type: 'adjust_meter' });

  const [initialValues, setInitialValues] = useState({
    imei: '',
    serial_no: '',
    meter_reading: '',
    customer_code: '',
    customer_name: '',
    model: '',
    firstValue: '',
    secondValue: '',
    note: ''
  });

  // Polling / magnet-confirm states
  const [magnetConfirmOpen, setMagnetConfirmOpen] = useState(false);
  const [pollingOpen, setPollingOpen] = useState(false);
  const [pollingStatus, setPollingStatus] = useState('polling'); // 'polling' | 'success' | 'failed' | 'timeout'
  const [remainingSeconds, setRemainingSeconds] = useState(180);

  const pendingSubmitRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const pollingTimeoutRef = useRef(null);
  const countdownRef = useRef(null);

  // ─── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = (id) => {
    Modal.confirm({
      title: 'Xác nhận',
      content: 'Bạn có chắc chắn muốn xóa lệnh này?',
      okText: 'Xóa',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          setIsDelete(true);
          await apiDeleteLinkDown(id);
          message.success('Xóa lệnh thành công');
          fetchHistory(appliedFilters);
        } catch (error) {
          console.error(error);
          message.error('Có lỗi xảy ra, vui lòng liên hệ admin');
        } finally {
          setIsDelete(false);
        }
      }
    });
  };

  // ─── Formik submit → only saves data and shows magnet confirm ──────────────
  const handleSubmit = (values, helpers) => {
    pendingSubmitRef.current = { values, helpers };
    setMagnetConfirmOpen(true);
  };

  // ─── Polling helpers ────────────────────────────────────────────────────────
  const stopPolling = () => {
    clearInterval(pollingIntervalRef.current);
    clearTimeout(pollingTimeoutRef.current);
    clearInterval(countdownRef.current);
    pollingIntervalRef.current = null;
    pollingTimeoutRef.current = null;
    countdownRef.current = null;
  };

  const startPolling = (imei) => {
    setRemainingSeconds(180);

    // 1-second countdown display
    countdownRef.current = setInterval(() => {
      setRemainingSeconds((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    // Poll every 5 seconds
    pollingIntervalRef.current = setInterval(async () => {
      try {
        const res = await apiGetLinkDown({
          page: 1,
          size: 1,
          type: 'adjust_meter',
          keyWord: imei
        });
        const item = res.data?.data?.[0];
        if (!item) return;

        // Success: device confirmed receipt
        if (item.is_sent === 2) {
          stopPolling();
          setPollingStatus('success');
          fetchHistory(appliedFilters);
          return;
        }

        // Failed: sent but not confirmed within 60 s
        const now = new Date();
        const sentAt = new Date(item.sent_at);
        if (item.is_sent === 1 && now.getTime() - 60000 > sentAt.getTime()) {
          stopPolling();
          setPollingStatus('failed');
          fetchHistory(appliedFilters);
        }
      } catch (err) {
        console.error(err);
      }
    }, 5000);

    // 3-minute hard timeout
    pollingTimeoutRef.current = setTimeout(() => {
      stopPolling();
      setPollingStatus('timeout');
      fetchHistory(appliedFilters);
    }, 3 * 60 * 1000);
  };

  // ─── Called after user clicks "Đã đặt" in magnet confirm modal ─────────────
  const handleConfirmMagnet = async () => {
    setMagnetConfirmOpen(false);
    if (!pendingSubmitRef.current) return;
    const {
      values,
      helpers: { resetForm }
    } = pendingSubmitRef.current;

    try {
      setIsSending(true);

      const firstFloatValue = values.firstValue.replace(/,/g, '') || '0';
      let secondFloatValue = values.secondValue.replace(/,/g, '') || '000';
      secondFloatValue = secondFloatValue.padEnd(3, '0');
      const meterStr =
        firstFloatValue + (isNullOrEmpty(secondFloatValue) ? '000' : secondFloatValue);

      const payload = {
        cmd: { setMeterReading: meterStr },
        imei: values.imei,
        serial_no: values.serial_no,
        customer_code: values.customer_code,
        customer_name: values.customer_name,
        image_url: uploadedImage?.url || null,
        note: values.note || null
      };

      await apiCreateLinkDown(payload);

      // Reset form after successful command creation
      const emptyValues = {
        imei: '',
        serial_no: '',
        meter_reading: '',
        customer_code: '',
        customer_name: '',
        model: '',
        firstValue: '',
        secondValue: '',
        note: ''
      };
      setUploadedImage(null);
      setInitialValues(emptyValues);
      resetForm({ values: emptyValues });

      // Open polling modal and start monitoring
      setPollingStatus('polling');
      setPollingOpen(true);
      startPolling(values.imei);
    } catch (e) {
      console.error(e);
      message.error('Có lỗi xảy ra, vui lòng liên hệ admin');
    } finally {
      setIsSending(false);
    }
  };

  const handleClosePolling = () => {
    stopPolling();
    setPollingOpen(false);
    setPollingStatus('polling');
    setRemainingSeconds(180);
  };

  // ─── Data fetching ──────────────────────────────────────────────────────────
  const fetchDevices = async () => {
    try {
      const res = await apiGetDevicesInformation({ page: 1, size: 2000 });
      setDeviceList(
        (res.data?.data ?? []).map((item) => ({
          label:
            'S/N: ' +
            item?.serial_no +
            '  |  KH: ' +
            (item?.customer_name || item?.customer_id || '\u2014'),
          value: item?.imei,
          ...item
        }))
      );
    } catch (err) {
      console.error(err);
    }
  };

  const fetchHistory = async (filters = {}) => {
    try {
      setLoading(true);
      const res = await apiGetLinkDown({ page: currentPage, size: pageSize, ...filters });
      setHistoricalCmd(res.data?.data ?? []);
      setTotalItems(res.data?.meta?.total ?? 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    const filters = { type: 'adjust_meter' };
    if (filterKeyword) filters.keyWord = filterKeyword;
    if (filterDateRange[0])
      filters.fromDate = filterDateRange[0].format('YYYY-MM-DD') + ' 00:00:00';
    if (filterDateRange[1]) filters.toDate = filterDateRange[1].format('YYYY-MM-DD') + ' 23:59:59';
    if (filterStatus !== '') filters.isSent = filterStatus;
    setAppliedFilters(filters);
    setCurrentPage(1);
    fetchHistory(filters);
  };

  const handleExportExcel = async () => {
    try {
      setLoading(true);
      await downloadBlobFile(
        `${URL_API}/api/down-link-command/export-adjust`,
        `Lich_su_hieu_chinh_thiet_bi.xlsx`,
        { ...appliedFilters },
        'GET'
      );
    } catch (e) {
      console.error(e);
      message.error('Có lỗi xảy ra khi xuất file, vui lòng thử lại');
    } finally {
      setLoading(false);
    }
  };

  // ─── Effects ────────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchDevices();
    fetchHistory({ type: 'adjust_meter' });
  }, []);

  useEffect(() => {
    fetchHistory(appliedFilters);
  }, [currentPage]);

  // Cleanup polling on unmount
  useEffect(() => () => stopPolling(), []);

  // ─── Table columns ──────────────────────────────────────────────────────────
  const historyColumns = [
    {
      title: 'STT',
      key: 'stt',
      width: 50,
      align: 'center',
      render: (_, __, index) => (currentPage - 1) * pageSize + index + 1
    },
    {
      title: 'Trạng thái',
      key: 'status',
      width: 110,
      render: (_, item) => getStatusTag(item)
    },
    {
      title: 'Serial',
      dataIndex: 'serial_no',
      key: 'serial_no',
      render: (v) => v || '\u2014'
    },
    {
      title: 'IMEI',
      dataIndex: 'imei',
      key: 'imei',
      render: (v) => v || '\u2014'
    },
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model',
      render: (v) => v || '\u2014'
    },
    {
      title: 'Khách hàng',
      key: 'customer',
      render: (_, item) => (
        <Space direction="vertical" size={1}>
          <Text style={{ fontSize: 12, color: 'var(--muted)' }}>
            {item.customer_code || '\u2014'}
          </Text>
          <Text style={{ fontSize: 13 }}>{item.customer_name || '\u2014'}</Text>
        </Space>
      )
    },
    {
      title: 'Thời điểm',
      key: 'created_at',
      width: 130,
      render: (_, item) => (
        <Text style={{ fontSize: 13 }}>{moment(item.created_at).format('DD/MM/YYYY HH:mm')}</Text>
      )
    },
    {
      title: 'Chỉ số',
      key: 'chi_so',
      align: 'right',
      width: 90,
      render: (_, item) => {
        let cmdObj = {};
        try {
          cmdObj = JSON.parse(item?.cmd || '{}');
        } catch {
          cmdObj = {};
        }
        const v = cmdObj?.setMeterReading;
        return v ? (
          <Text style={{ color: '#0284c7', fontWeight: 500 }}>{(Number(v) / 1000).toFixed(3)}</Text>
        ) : (
          '\u2014'
        );
      }
    },
    {
      title: 'Ảnh',
      key: 'image',
      width: 80,
      align: 'center',
      render: (_, item) =>
        item?.image_url ? (
          <a
            href={item.image_url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#0284c7', textDecoration: 'underline', fontSize: 12 }}>
            Xem ảnh
          </a>
        ) : (
          <Text style={{ color: '#bfbfbf', fontSize: 11 }}>Không có ảnh</Text>
        )
    },
    {
      title: 'Người tạo',
      dataIndex: 'created_by',
      key: 'created_by',
      width: 100,
      render: (v) => v || '\u2014'
    },
    {
      title: 'Ghi chú',
      dataIndex: 'note',
      key: 'note',
      render: (note) =>
        note ? (
          <Tooltip title={note}>
            <Text ellipsis style={{ maxWidth: 140, fontSize: 12 }}>
              {note}
            </Text>
          </Tooltip>
        ) : (
          '\u2014'
        )
    },
    {
      title: 'Thao tác',
      key: 'action',
      width: 100,
      align: 'center',
      render: (_, item) => (
        <Space size={4} direction="vertical">
          <Button
            type="link"
            size="small"
            style={{ padding: 0, height: 'auto' }}
            onClick={() => setDetailRecord(item)}>
            Xem chi tiết
          </Button>
          {adjustMeterReading.modify && item?.is_sent === 0 && (
            <Button
              type="text"
              icon={<DeleteOutlined />}
              danger
              size="small"
              loading={isDelete}
              onClick={() => handleDelete(Number(item?.id))}
            />
          )}
        </Space>
      )
    }
  ];

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      {/* FORM CARD */}
      <Card
        title={
          <Text strong style={{ fontSize: 16 }}>
            Thông tin hiệu chỉnh
          </Text>
        }
        style={{ marginBottom: 24 }}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize>
          {({ values, handleSubmit: formikSubmit, setFieldValue, errors, touched, resetForm }) => {
            useEffect(() => {
              setFieldValue('imei', initialValues.imei);
              setFieldValue('serial_no', initialValues.serial_no);
              setFieldValue('meter_reading', initialValues.meter_reading);
              setFieldValue('customer_code', initialValues.customer_code);
              setFieldValue('customer_name', initialValues.customer_name);
              setFieldValue('model', initialValues.model);
            }, [initialValues]);

            const handleReset = () => {
              setInitialValues({
                imei: '',
                serial_no: '',
                meter_reading: '',
                customer_code: '',
                customer_name: '',
                model: '',
                firstValue: '',
                secondValue: '',
                note: ''
              });
              setUploadedImage(null);
              resetForm();
            };

            return (
              <Row gutter={24}>
                {/* LEFT: Form */}
                <Col xs={24} lg={16}>
                  <Form.Item
                    label={<Text strong>Chọn thiết bị</Text>}
                    required
                    labelCol={{ span: 24 }}
                    validateStatus={touched.imei && errors.imei ? 'error' : ''}
                    help={touched.imei && errors.imei ? errors.imei : null}
                    style={{ marginBottom: 12 }}>
                    <Select
                      showSearch
                      allowClear
                      filterOption={(input, option) =>
                        option?.label?.toLowerCase().includes(input.toLowerCase())
                      }
                      style={{ width: '100%' }}
                      placeholder="Chọn thiết bị"
                      value={values.imei || null}
                      onChange={(value, option) => {
                        if (!value) {
                          setInitialValues({
                            imei: '',
                            serial_no: '',
                            meter_reading: '',
                            customer_code: '',
                            customer_name: '',
                            model: '',
                            firstValue: '',
                            secondValue: '',
                            note: ''
                          });
                          const resetFilters = { type: 'adjust_meter' };
                          setAppliedFilters(resetFilters);
                          setCurrentPage(1);
                          fetchHistory(resetFilters);
                          return;
                        }
                        setInitialValues((prev) => ({
                          ...prev,
                          imei: value,
                          serial_no: option.serial_no || '',
                          meter_reading: option.meter_reading || '',
                          customer_code: option.customer_id || '',
                          customer_name: option.customer_name || '',
                          model: option.model || ''
                        }));
                        const imeiFilters = { type: 'adjust_meter', keyWord: value };
                        setAppliedFilters(imeiFilters);
                        setCurrentPage(1);
                        fetchHistory(imeiFilters);
                      }}
                      options={deviceList}
                    />
                  </Form.Item>

                  <Form.Item
                    label={<Text strong>Thời điểm</Text>}
                    required
                    labelCol={{ span: 24 }}
                    style={{ marginBottom: 12 }}>
                    <DatePicker
                      showTime
                      format="DD/MM/YYYY HH:mm:ss"
                      defaultValue={dayjs()}
                      style={{ width: '100%' }}
                      allowClear={false}
                      disabled
                    />
                  </Form.Item>

                  {/* Device info box */}
                  <div
                    style={{
                      background: 'var(--surface-2)',
                      border: '1px solid var(--border)',
                      borderRadius: 6,
                      padding: '10px 14px',
                      marginBottom: 12
                    }}>
                    <Text style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 500 }}>
                      Thông tin thiết bị
                    </Text>
                    <Row gutter={16} style={{ marginTop: 6 }}>
                      <Col span={12}>
                        <Text style={{ fontSize: 12 }}>
                          Serial: <strong>{values.serial_no || '\u2014'}</strong>
                        </Text>
                      </Col>
                      <Col span={12}>
                        <Text style={{ fontSize: 12 }}>
                          IMEI: <strong>{values.imei || '\u2014'}</strong>
                        </Text>
                      </Col>
                      <Col span={12} style={{ marginTop: 4 }}>
                        <Text style={{ fontSize: 12 }}>
                          Model: <strong>{values.model || '\u2014'}</strong>
                        </Text>
                      </Col>
                      <Col span={12} style={{ marginTop: 4 }}>
                        <Space size={4}>
                          <Text style={{ fontSize: 12 }}>Trạng thái lắp đặt:</Text>
                          <span
                            style={{
                              display: 'inline-block',
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              background: values.imei ? '#10b981' : '#d9d9d9'
                            }}
                          />
                        </Space>
                      </Col>
                    </Row>
                    <div style={{ marginTop: 4 }}>
                      <Text style={{ fontSize: 12, fontStyle: 'italic', color: '#D48806' }}>
                        Đang lắp cho khách hàng: <strong>{values.customer_name || '\u2014'}</strong>
                        {values.customer_code ? ' (' + values.customer_code + ')' : ''}
                      </Text>
                    </div>
                  </div>

                  {/* Số khối + Số lít */}
                  <Row gutter={8} align="bottom" style={{ marginBottom: 4 }}>
                    <Col flex={1}>
                      <Form.Item
                        label={<Text strong>Số khối (m³)</Text>}
                        required
                        labelCol={{ span: 24 }}
                        validateStatus={touched.firstValue && errors.firstValue ? 'error' : ''}
                        help={touched.firstValue && errors.firstValue ? errors.firstValue : null}
                        style={{ marginBottom: 0 }}>
                        <Input
                          placeholder="Nhập số khối"
                          value={values.firstValue}
                          onChange={(e) =>
                            setFieldValue('firstValue', formatNumber(e.target.value))
                          }
                        />
                      </Form.Item>
                    </Col>
                    <Col style={{ paddingBottom: 2 }}>
                      <Text style={{ fontSize: 22, lineHeight: '32px' }}>.</Text>
                    </Col>
                    <Col flex={1}>
                      <Form.Item
                        label={<Text strong>Số lít (L)</Text>}
                        labelCol={{ span: 24 }}
                        validateStatus={touched.secondValue && errors.secondValue ? 'error' : ''}
                        help={touched.secondValue && errors.secondValue ? errors.secondValue : null}
                        style={{ marginBottom: 0 }}>
                        <Input
                          placeholder="Nhập số lít"
                          value={values.secondValue}
                          onChange={(e) =>
                            setFieldValue('secondValue', formatNumber(e.target.value))
                          }
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    label={<Text strong>Ảnh chụp chỉ số</Text>}
                    required
                    labelCol={{ span: 24 }}
                    style={{ marginBottom: 12, marginTop: 12 }}>
                    <InputUploadImage
                      onChange={(file) => setUploadedImage(file)}
                      initValue={uploadedImage}
                    />
                  </Form.Item>

                  <Form.Item
                    label={<Text strong>Ghi chú</Text>}
                    labelCol={{ span: 24 }}
                    style={{ marginBottom: 16 }}>
                    <Input.TextArea
                      rows={2}
                      placeholder="Nhập ghi chú..."
                      value={values.note}
                      onChange={(e) => setFieldValue('note', e.target.value)}
                      maxLength={500}
                      showCount
                    />
                  </Form.Item>

                  <Space wrap>
                    <Button onClick={handleReset}>Đặt lại</Button>
                    {adjustMeterReading.modify && (
                      <Button
                        type="primary"
                        icon={<ToolOutlined />}
                        onClick={formikSubmit}
                        loading={isSending}
                        style={{ backgroundColor: '#0284c7' }}>
                        Gửi lệnh
                      </Button>
                    )}
                  </Space>
                </Col>

                {/* RIGHT: Instruction guide */}
                <Col xs={24} lg={8} style={{ marginTop: 0 }}>
                  <div
                    style={{
                      background: 'color-mix(in srgb, #e11d48 9%, var(--surface))',
                      border: '1px solid color-mix(in srgb, #e11d48 28%, var(--border))',
                      borderRadius: 6,
                      padding: '14px 16px',
                      color: 'var(--text)'
                    }}>
                    <Text
                      strong
                      style={{
                        fontSize: 14,
                        color: 'color-mix(in srgb, #e11d48 70%, var(--text))'
                      }}>
                      Hướng dẫn nhập chỉ số:
                    </Text>
                    <div style={{ marginTop: 10, fontSize: 13, lineHeight: 1.8 }}>
                      <div>
                        Ví dụ chỉ số đồng hồ hiển thị là: <Text strong>12345.678</Text>
                      </div>
                      <div style={{ marginTop: 4 }}>Nhập chỉ số dữ liệu sau:</div>
                      <ul style={{ marginTop: 4, paddingLeft: 18, marginBottom: 8 }}>
                        <li>
                          <strong>Số khối (m³):</strong> 12345
                        </li>
                        <li>
                          <strong>Số lít (L):</strong> 678 (không nhập dấu chấm và số 0)
                        </li>
                      </ul>
                      <div style={{ color: 'var(--muted)', fontStyle: 'italic', fontSize: 12 }}>
                        Sau khi ấn <strong>Gửi lệnh</strong>, kích hoạt thiết bị từ xa và quan sát
                        trạng thái lệnh trả về.
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            );
          }}
        </Formik>
      </Card>

      {/* HISTORY SECTION */}
      <div>
        <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
          <Col>
            <Title level={5} style={{ margin: 0 }}>
              Lịch sử gửi lệnh
            </Title>
            <Text style={{ fontSize: 12, color: '#8c8c8c' }}>
              Danh sách các lệnh hiệu chỉnh/cài đặt chỉ số đã gửi
            </Text>
          </Col>
          <Col>
            {adjustMeterReading.export && (
              <Button
                icon={<FileExcelOutlined />}
                style={{ color: '#10b981', borderColor: '#10b981' }}
                loading={loading}
                onClick={handleExportExcel}>
                Xuất file Excel
              </Button>
            )}
          </Col>
        </Row>

        <Row gutter={[8, 8]} style={{ marginBottom: 12 }}>
          <Col xs={24} md={8} lg={7}>
            <Input
              placeholder="Tìm kiếm serial, imei, model..."
              value={filterKeyword}
              onChange={(e) => setFilterKeyword(e.target.value)}
              onPressEnter={handleSearch}
              allowClear
            />
          </Col>
          <Col xs={24} md={8} lg={8}>
            <RangePicker
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
              value={filterDateRange}
              onChange={(dates) => setFilterDateRange(dates || [null, null])}
              placeholder={['Từ ngày', 'Đến ngày']}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={4} lg={5}>
            <Select
              style={{ width: '100%' }}
              placeholder="Trạng thái"
              value={filterStatus === '' ? undefined : filterStatus}
              allowClear
              onChange={(v) => setFilterStatus(v ?? '')}
              options={[
                { label: 'Tất cả', value: '' },
                { label: 'Chưa gửi', value: '0' },
                { label: 'Đang gửi', value: '1' },
                { label: 'Đã gửi', value: '2' }
              ]}
            />
          </Col>
          <Col xs={24} sm={12} md={4} lg={4}>
            <Button
              type="primary"
              block
              onClick={handleSearch}
              style={{ backgroundColor: '#0284c7' }}>
              Tìm kiếm
            </Button>
          </Col>
        </Row>

        <Table
          rowKey="id"
          loading={loading}
          dataSource={historicalCmd}
          columns={historyColumns}
          size="small"
          scroll={{ x: 1100 }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalItems,
            showSizeChanger: false,
            showTotal: (total, range) =>
              'Hiển thị ' + range[0] + ' - ' + range[1] + ' trong ' + total + ' bản ghi',
            onChange: (page) => setCurrentPage(page)
          }}
        />
      </div>

      {/* DETAIL MODAL */}
      <Modal
        open={!!detailRecord}
        title="Chi tiết lệnh hiệu chỉnh"
        footer={<Button onClick={() => setDetailRecord(null)}>Đóng</Button>}
        onCancel={() => setDetailRecord(null)}
        style={{ maxWidth: 560 }}
        width="95%">
        {detailRecord &&
          (() => {
            let cmdObj = {};
            try {
              cmdObj = JSON.parse(detailRecord?.cmd || '{}');
            } catch {
              cmdObj = {};
            }
            const adjustValue = cmdObj?.setMeterReading;
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {/* Trạng thái */}
                <div style={{ textAlign: 'center', padding: '16px 0 12px' }}>
                  {getStatusTag(detailRecord)}
                </div>

                {/* Chỉ số hiệu chỉnh nổi bật */}
                <div
                  style={{
                    background: 'color-mix(in srgb, #0284c7 12%, var(--surface))',
                    border: '1px solid color-mix(in srgb, #0284c7 32%, var(--border))',
                    borderRadius: 8,
                    padding: '12px 16px',
                    marginBottom: 16,
                    textAlign: 'center'
                  }}>
                  <Text style={{ fontSize: 12, color: 'var(--muted)' }}>Chỉ số hiệu chỉnh</Text>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#0284c7', marginTop: 2 }}>
                    {adjustValue ? `${(Number(adjustValue) / 1000).toFixed(3)} m³` : '—'}
                  </div>
                </div>

                {/* Thông tin thiết bị */}
                <div style={{ marginBottom: 12 }}>
                  <Text style={sectionLabelStyle}>Thiết bị</Text>
                  <Row gutter={[12, 6]} style={{ marginTop: 6 }}>
                    <Col span={12}>
                      <Text style={{ fontSize: 12, color: '#8c8c8c' }}>Serial</Text>
                      <div style={{ fontWeight: 500 }}>{detailRecord.serial_no || '—'}</div>
                    </Col>
                    <Col span={12}>
                      <Text style={{ fontSize: 12, color: '#8c8c8c' }}>IMEI</Text>
                      <div style={{ fontWeight: 500 }}>{detailRecord.imei || '—'}</div>
                    </Col>
                    <Col span={12}>
                      <Text style={{ fontSize: 12, color: '#8c8c8c' }}>Model</Text>
                      <div style={{ fontWeight: 500 }}>{detailRecord.model || '—'}</div>
                    </Col>
                  </Row>
                </div>

                {/* Khách hàng */}
                <div style={{ marginBottom: 12 }}>
                  <Text style={sectionLabelStyle}>Khách hàng</Text>
                  <Row gutter={[12, 6]} style={{ marginTop: 6 }}>
                    <Col span={12}>
                      <Text style={{ fontSize: 12, color: '#8c8c8c' }}>Mã KH</Text>
                      <div style={{ fontWeight: 500 }}>{detailRecord.customer_code || '—'}</div>
                    </Col>
                    <Col span={12}>
                      <Text style={{ fontSize: 12, color: '#8c8c8c' }}>Tên KH</Text>
                      <div style={{ fontWeight: 500 }}>{detailRecord.customer_name || '—'}</div>
                    </Col>
                  </Row>
                </div>

                {/* Thời gian & người tạo */}
                <div style={{ marginBottom: 12 }}>
                  <Text style={sectionLabelStyle}>Thông tin lệnh</Text>
                  <Row gutter={[12, 6]} style={{ marginTop: 6 }}>
                    <Col span={12}>
                      <Text style={{ fontSize: 12, color: '#8c8c8c' }}>Người tạo</Text>
                      <div style={{ fontWeight: 500 }}>{detailRecord.created_by || '—'}</div>
                    </Col>
                    <Col span={12}>
                      <Text style={{ fontSize: 12, color: '#8c8c8c' }}>Thời gian tạo</Text>
                      <div style={{ fontWeight: 500 }}>
                        {moment(detailRecord.created_at).format('DD/MM/YYYY HH:mm:ss')}
                      </div>
                    </Col>
                    {detailRecord.sent_at && (
                      <Col span={12}>
                        <Text style={{ fontSize: 12, color: '#8c8c8c' }}>Thời gian gửi</Text>
                        <div style={{ fontWeight: 500 }}>
                          {moment(detailRecord.sent_at).format('DD/MM/YYYY HH:mm:ss')}
                        </div>
                      </Col>
                    )}
                  </Row>
                </div>

                {/* Ghi chú */}
                {detailRecord.note && (
                  <div style={{ marginBottom: 12 }}>
                    <Text style={sectionLabelStyle}>Ghi chú</Text>
                    <div
                      style={{
                        marginTop: 6,
                        padding: '8px 10px',
                        background: 'var(--surface-2)',
                        border: '1px solid var(--border)',
                        borderRadius: 6
                      }}>
                      {detailRecord.note}
                    </div>
                  </div>
                )}

                {/* Ảnh minh chứng */}
                {detailRecord.image_url && (
                  <div>
                    <Text style={sectionLabelStyle}>Ảnh minh chứng</Text>
                    <div style={{ marginTop: 6 }}>
                      <a href={detailRecord.image_url} target="_blank" rel="noopener noreferrer">
                        Xem ảnh
                      </a>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
      </Modal>

      {/* MAGNET CONFIRM MODAL */}
      <Modal
        open={magnetConfirmOpen}
        title="Kích hoạt thiết bị"
        maskClosable={false}
        onCancel={() => setMagnetConfirmOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setMagnetConfirmOpen(false)}>
            Huỷ
          </Button>,
          <Button
            key="confirm"
            type="primary"
            loading={isSending}
            onClick={handleConfirmMagnet}
            style={{ backgroundColor: '#0284c7' }}>
            Đã đặt
          </Button>
        ]}
        width={420}>
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <ExclamationCircleOutlined style={{ fontSize: 52, color: '#f59e0b' }} />
          <div style={{ marginTop: 16, fontSize: 15, fontWeight: 600 }}>
            Bạn hãy đặt nam châm để kích hoạt thiết bị nhận lệnh
          </div>
          <div style={{ marginTop: 10, color: 'var(--muted)', fontSize: 13 }}>
            Sau khi đã đặt nam châm vào vị trí kích hoạt, nhấn{' '}
            <Text strong style={{ color: '#0284c7' }}>
              Đã đặt
            </Text>{' '}
            để tiến hành gửi lệnh hiệu chỉnh.
          </div>
        </div>
      </Modal>

      {/* POLLING STATUS MODAL */}
      <Modal
        open={pollingOpen}
        title="Trạng thái gửi lệnh"
        closable={pollingStatus !== 'polling'}
        maskClosable={false}
        onCancel={handleClosePolling}
        footer={
          pollingStatus === 'polling'
            ? []
            : [
                <Button key="close" type="primary" onClick={handleClosePolling}>
                  Đóng
                </Button>
              ]
        }
        width={420}>
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          {pollingStatus === 'polling' && (
            <>
              <div style={{ marginTop: 4, fontSize: 15, fontWeight: 500 }}>
                Đang chờ thiết bị nhận lệnh...
              </div>
              <div style={{ margin: '20px 0 4px', position: 'relative' }}>
                <Progress
                  percent={Math.round(((180 - remainingSeconds) / 180) * 100)}
                  status={remainingSeconds < 30 ? 'exception' : 'active'}
                  strokeColor={
                    remainingSeconds < 30 ? '#e11d48' : { '0%': '#0284c7', '100%': '#36cfc9' }
                  }
                  format={(percent) => (
                    <Text
                      strong
                      style={{
                        color: remainingSeconds < 30 ? '#e11d48' : '#0284c7',
                        fontSize: 13
                      }}>
                      {percent}%
                    </Text>
                  )}
                />
              </div>
              <div style={{ marginTop: 8, color: 'var(--muted)', fontSize: 12 }}>
                Lệnh đang gửi vui lòng chờ giây lát
              </div>
            </>
          )}

          {pollingStatus === 'success' && (
            <>
              <CheckCircleOutlined style={{ fontSize: 56, color: '#10b981' }} />
              <div style={{ marginTop: 16, fontSize: 17, fontWeight: 700, color: '#10b981' }}>
                Gửi lệnh thành công!
              </div>
              <div style={{ marginTop: 8, color: 'var(--muted)', fontSize: 13 }}>
                Thiết bị đã nhận và xác nhận lệnh hiệu chỉnh chỉ số.
              </div>
            </>
          )}

          {(pollingStatus === 'failed' || pollingStatus === 'timeout') && (
            <>
              <CloseCircleOutlined style={{ fontSize: 56, color: '#e11d48' }} />
              <div style={{ marginTop: 16, fontSize: 17, fontWeight: 700, color: '#e11d48' }}>
                {pollingStatus === 'timeout' ? 'Quá thời gian chờ' : 'Gửi lệnh thất bại'}
              </div>
              <div style={{ marginTop: 8, color: 'var(--muted)', fontSize: 13 }}>
                {pollingStatus === 'timeout'
                  ? 'Thiết bị không phản hồi trong 3 phút. Vui lòng kiểm tra kết nối và thử lại.'
                  : 'Thiết bị đã nhận lệnh nhưng không xác nhận thành công. Vui lòng kiểm tra và hiệu chỉnh lại.'}
              </div>
            </>
          )}
        </div>
      </Modal>
    </>
  );
}
